import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  LinearProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Tooltip,
  Chip,
  Card,
  CardContent,
  Stack,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  CloudDownload as DownloadIcon,
  SaveOutlined as SaveIcon,
  WarningAmber as WarningIcon,
  ErrorOutline as ErrorIcon,
  CheckCircleOutline as CheckCircleIcon,
  InfoOutlined as InfoIcon
} from '@mui/icons-material';
import * as XLSX from 'xlsx';
import { getProducts, getBulkUpdateTemplate, downloadBulkUpdateExcelTemplate, bulkUpdateProducts } from '../../services/productService';
import { Product } from '../../types/product';

// واجهة لتمثيل تغييرات المنتج
interface ProductChange {
  productId: string;
  productName: string;
  variantId: string;
  barcodeId: string;
  barcode: string;
  additionalCodes?: string[];
  retailPrice?: number;
  wholesalePrice?: number;
  lastCost?: number;
  customPrice1?: number;
  customPrice2?: number;
  originalValues: {
    barcode: string;
    additionalCodes?: string[];
    retailPrice?: number;
    wholesalePrice?: number;
    lastCost?: number;
    customPrice1?: number;
    customPrice2?: number;
  };
  isChanged: boolean;
  changes: string[];
}

const ProductBulkUpdate: React.FC = () => {
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingTemplate, setLoadingTemplate] = useState<boolean>(false);
  const [progress, setProgress] = useState<number>(0);
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [errors, setErrors] = useState<string[]>([]);
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [productChanges, setProductChanges] = useState<ProductChange[]>([]);
  const [showConfirmation, setShowConfirmation] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploaded, setIsUploaded] = useState<boolean>(false);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      setFile(event.target.files[0]);
      setErrors([]);
      setSuccessMessage('');
    }
  };

  const resetForm = () => {
    setFile(null);
    setProductChanges([]);
    setErrors([]);
    setSuccessMessage('');
    setProgress(0);
    setIsUploaded(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const downloadTemplate = async () => {
    try {
      setLoadingTemplate(true);
      setErrors([]);
      
      // الحصول على قالب الإكسل من الخادم ثم تنزيله
      const templateData = await getBulkUpdateTemplate();
      
      if (templateData && templateData.success && templateData.data) {
        // تنزيل البيانات كملف إكسل
        downloadBulkUpdateExcelTemplate(templateData.data);
        setSuccessMessage('تم تنزيل قالب الإكسل بنجاح');
      } else {
        setErrors(prev => [...prev, 'لم يتم العثور على بيانات لتنزيلها']);
      }
    } catch (error) {
      console.error('Error downloading template:', error);
      setErrors(prev => [...prev, 'فشل في تنزيل قالب الإكسل. الرجاء المحاولة مرة أخرى.']);
    } finally {
      setLoadingTemplate(false);
    }
  };

  const uploadExcelFile = async () => {
    if (!file) {
      setErrors(['الرجاء اختيار ملف أولاً.']);
      return;
    }
    
    setLoading(true);
    setProgress(10);
    setErrors([]);
    
    try {
      const reader = new FileReader();
      
      reader.onload = async (e) => {
        try {
          setProgress(30);
          const data = new Uint8Array(e.target?.result as ArrayBuffer);
          const workbook = XLSX.read(data, { type: 'array' });
          
          // استخدام الورقة الأولى
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];
          
          // تحويل البيانات إلى JSON
          const jsonData = XLSX.utils.sheet_to_json(worksheet);
          
          if (jsonData.length === 0) {
            setErrors(['الملف لا يحتوي على بيانات.']);
            setLoading(false);
            return;
          }
          
          setProgress(60);
          
          // تجهيز البيانات للعرض
          // هنا نحتاج إلى استخراج التغييرات ومقارنتها بالقيم الأصلية
          // لأغراض العرض فقط، لن نقوم بتحديث البيانات الآن
          const changes: ProductChange[] = [];
          
          for (const item of jsonData as any[]) {
            // إضافة المنتج إلى قائمة التغييرات مع مقارنة القيم
            // هذه تقريب لعرض التغييرات فقط
            const productChange: ProductChange = {
              productId: item.productId || '',
              productName: item.productName || item.name || '',
              variantId: item.variantId || '',
              barcodeId: item.barcodeId || '',
              barcode: item.barcode || '',
              additionalCodes: item.additionalBarcode
                ? (() => {
                    const barcodeStr = item.additionalBarcode.toString();
                    const codes = barcodeStr.split(',').map((code: string) => code.trim()).filter(code => code !== '');
                    console.log('🔍 معالجة الباركود الإضافي الحالي:', {
                      raw: item.additionalBarcode,
                      type: typeof item.additionalBarcode,
                      processed: codes
                    });
                    return codes;
                  })()
                : [],
              retailPrice: item.retailPrice !== undefined ? parseFloat(item.retailPrice) : undefined,
              wholesalePrice: item.wholesalePrice !== undefined ? parseFloat(item.wholesalePrice) : undefined,
              lastCost: item.lastCost !== undefined ? parseFloat(item.lastCost) : undefined,
              customPrice1: item.customPrice1 !== undefined ? parseFloat(item.customPrice1) : undefined,
              customPrice2: item.customPrice2 !== undefined ? parseFloat(item.customPrice2) : undefined,
              originalValues: {
                barcode: item.originalBarcode || '',
                additionalCodes: item.originalAdditionalBarcode
                  ? (() => {
                      const barcodeStr = item.originalAdditionalBarcode.toString();
                      const codes = barcodeStr.split(',').map((code: string) => code.trim()).filter(code => code !== '');
                      console.log('🔍 معالجة الباركود الإضافي الأصلي:', {
                        raw: item.originalAdditionalBarcode,
                        type: typeof item.originalAdditionalBarcode,
                        processed: codes
                      });
                      return codes;
                    })()
                  : [],
                retailPrice: item.originalRetailPrice !== undefined ? parseFloat(item.originalRetailPrice) : undefined,
                wholesalePrice: item.originalWholesalePrice !== undefined ? parseFloat(item.originalWholesalePrice) : undefined,
                lastCost: item.originalLastCost !== undefined ? parseFloat(item.originalLastCost) : undefined,
                customPrice1: item.originalCustomPrice1 !== undefined ? parseFloat(item.originalCustomPrice1) : undefined,
                customPrice2: item.originalCustomPrice2 !== undefined ? parseFloat(item.originalCustomPrice2) : undefined
              },
              isChanged: false,
              changes: []
            };
            
            // تحديد ما إذا كانت هناك تغييرات - مع التعامل الصحيح مع القيم الفارغة
            
            // إضافة console.log لتتبع المشكلة
            console.log('🔍 فحص المنتج:', {
              barcode: item.barcode,
              originalBarcode: item.originalBarcode,
              additionalBarcode: item.additionalBarcode,
              originalAdditionalBarcode: item.originalAdditionalBarcode,
              retailPrice: item.retailPrice,
              originalRetailPrice: item.originalRetailPrice,
              wholesalePrice: item.wholesalePrice,
              originalWholesalePrice: item.originalWholesalePrice,
              lastCost: item.lastCost,
              originalLastCost: item.originalLastCost,
              allKeys: Object.keys(item)
            });
            
            // مقارنة الباركود
            const currentBarcode = (item.barcode || '').toString().trim();
            const originalBarcode = (item.originalBarcode || '').toString().trim();
            if (currentBarcode !== originalBarcode) {
              productChange.changes.push('barcode');
              console.log('✅ تغيير في الباركود:', { current: currentBarcode, original: originalBarcode });
            }
            
            // مقارنة الباركودات الإضافية
            const currentAdditionalBarcode = (item.additionalBarcode || '').toString().trim();
            const originalAdditionalBarcode = (item.originalAdditionalBarcode || '').toString().trim();
            if (currentAdditionalBarcode !== originalAdditionalBarcode) {
              productChange.changes.push('additionalCodes');
            }
            
            // مقارنة الأسعار - التعامل مع القيم الفارغة والـ undefined
            const comparePrice = (current: any, original: any): boolean => {
              // تحويل القيم إلى أرقام أو null إذا كانت فارغة
              const currentPrice = (current === undefined || current === null || current === '') ? null : parseFloat(current);
              const originalPrice = (original === undefined || original === null || original === '') ? null : parseFloat(original);
              
              // إذا كانت إحدى القيم null والأخرى ليست null
              if (currentPrice === null && originalPrice !== null) return true;
              if (currentPrice !== null && originalPrice === null) return true;
              
              // إذا كانت كلا القيمتين null
              if (currentPrice === null && originalPrice === null) return false;
              
              // مقارنة الأرقام مع تقريب لتجنب مشاكل الفاصلة العشرية
              return Math.abs(currentPrice! - originalPrice!) > 0.001;
            };
            
            if (comparePrice(item.retailPrice, item.originalRetailPrice)) {
              productChange.changes.push('retailPrice');
              console.log('✅ تغيير في سعر التجزئة:', { current: item.retailPrice, original: item.originalRetailPrice });
            }
            
            if (comparePrice(item.wholesalePrice, item.originalWholesalePrice)) {
              productChange.changes.push('wholesalePrice');
              console.log('✅ تغيير في سعر الجملة:', { current: item.wholesalePrice, original: item.originalWholesalePrice });
            }
            
            if (comparePrice(item.lastCost, item.originalLastCost)) {
              productChange.changes.push('lastCost');
              console.log('✅ تغيير في آخر تكلفة:', { current: item.lastCost, original: item.originalLastCost });
            }
            
            if (comparePrice(item.customPrice1, item.originalCustomPrice1)) {
              productChange.changes.push('customPrice1');
              console.log('✅ تغيير في السعر المخصص 1:', { current: item.customPrice1, original: item.originalCustomPrice1 });
            }
            
            if (comparePrice(item.customPrice2, item.originalCustomPrice2)) {
              productChange.changes.push('customPrice2');
              console.log('✅ تغيير في السعر المخصص 2:', { current: item.customPrice2, original: item.originalCustomPrice2 });
            }
            
            productChange.isChanged = productChange.changes.length > 0;
            
            console.log('📊 نتيجة المقارنة:', {
              barcode: item.barcode,
              isChanged: productChange.isChanged,
              changes: productChange.changes,
              totalChanges: productChange.changes.length
            });
            
            // إضافة المنتج للقائمة فقط إذا كان له باركود وتم تغييره فعلياً
            if (item.barcode && productChange.isChanged) {
              changes.push(productChange);
              console.log('✅ تمت إضافة المنتج للقائمة:', item.barcode);
            } else if (item.barcode && !productChange.isChanged) {
              console.log('❌ لم تتم إضافة المنتج - لا توجد تغييرات:', item.barcode);
            }
          }
          
          setProductChanges(changes);
          setIsUploaded(true);
          
          const changedProductsCount = changes.filter(change => change.isChanged).length;
          setSuccessMessage(`تم تحليل الملف بنجاح. تم العثور على ${changedProductsCount} منتج/منتجات تم تعديلها.`);
          
        } catch (error) {
          console.error('Error parsing Excel file:', error);
          setErrors(['حدث خطأ أثناء معالجة ملف Excel. تأكد من أن الملف بالتنسيق الصحيح.']);
        } finally {
          setProgress(100);
          setLoading(false);
        }
      };
      
      reader.onerror = () => {
        setErrors(['حدث خطأ أثناء قراءة الملف.']);
        setLoading(false);
      };
      
      reader.readAsArrayBuffer(file);
      
    } catch (error) {
      console.error('Error uploading file:', error);
      setErrors(['حدث خطأ أثناء رفع الملف.']);
      setLoading(false);
    }
  };

  const confirmSave = () => {
    setShowConfirmation(true);
  };

  const saveChanges = async () => {
    setLoading(true);
    setProgress(10);
    setErrors([]);
    
    try {
      // فلترة التغييرات لإرسال فقط المنتجات التي تم تغييرها
      const changedProducts = productChanges.filter(change => change.isChanged);
      
      if (changedProducts.length === 0) {
        setErrors(['لا توجد تغييرات للحفظ.']);
        setLoading(false);
        setShowConfirmation(false);
        return;
      }
      
      // إعداد بيانات المنتجات للإرسال
      const productsToUpdate = changedProducts.map(change => ({
        barcode: change.originalValues.barcode, // الباركود الأصلي للبحث
        newBarcode: change.barcode, // الباركود الجديد للتحديث
        additionalBarcode: change.additionalCodes ? change.additionalCodes.join(', ') : '',
        retailPrice: change.retailPrice,
        wholesalePrice: change.wholesalePrice,
        lastCost: change.lastCost,
        customPrice1: change.customPrice1,
        customPrice2: change.customPrice2
      }));
      
      // إرسال التحديث إلى الخادم
      const response = await bulkUpdateProducts(productsToUpdate);
      
      if (response && response.success) {
        setSuccessMessage(`تم تحديث ${response.updatedCount} منتج/منتجات بنجاح.`);
        setProductChanges([]);
        setIsUploaded(false);
        setFile(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      } else {
        setErrors(['فشل في تحديث المنتجات. الرجاء المحاولة مرة أخرى.']);
      }
      
    } catch (error: any) {
      console.error('Error saving changes:', error);
      setErrors([error.message || 'حدث خطأ أثناء حفظ التغييرات.']);
    } finally {
      setProgress(100);
      setLoading(false);
      setShowConfirmation(false);
    }
  };

  const renderChangesTable = () => {
    // فلترة التغييرات لعرض فقط المنتجات التي تم تغييرها
    const changedProducts = productChanges.filter(change => change.isChanged);
    
    return (
      <TableContainer component={Paper} sx={{ mt: 3, mb: 3 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>اسم المنتج</TableCell>
              <TableCell>رمز الباركود</TableCell>
              <TableCell>باركود الإضافي</TableCell>
              <TableCell>سعر التجزئة</TableCell>
              <TableCell>سعر الجملة</TableCell>
              <TableCell>آخر تكلفة</TableCell>
              <TableCell>سعر مخصص 1</TableCell>
              <TableCell>سعر مخصص 2</TableCell>
              <TableCell>التغييرات</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {changedProducts
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((change, index) => (
                <TableRow 
                  key={index}
                  sx={{ backgroundColor: change.isChanged ? 'rgba(255, 244, 229, 0.5)' : 'transparent' }}
                >
                  <TableCell>
                    {change.productName || '-'}
                  </TableCell>
                  <TableCell>
                    {change.barcode}
                    {change.changes.includes('barcode') && (
                      <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                        <Typography variant="caption" sx={{ color: 'text.secondary', textDecoration: 'line-through', mr: 1 }}>
                          {change.originalValues.barcode}
                        </Typography>
                        <Chip size="small" label="تم التغيير" color="warning" variant="outlined" />
                      </Box>
                    )}
                  </TableCell>
                  <TableCell>
                    {change.additionalCodes && change.additionalCodes.length > 0
                      ? change.additionalCodes.join(', ')
                      : '-'}
                    {change.changes.includes('additionalCodes') && (
                      <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                        <Typography variant="caption" sx={{ color: 'text.secondary', textDecoration: 'line-through', mr: 1 }}>
                          {change.originalValues.additionalCodes && change.originalValues.additionalCodes.length > 0
                            ? change.originalValues.additionalCodes.join(', ')
                            : '-'}
                        </Typography>
                        <Chip size="small" label="تم التغيير" color="warning" variant="outlined" />
                      </Box>
                    )}
                  </TableCell>
                  <TableCell>
                    {change.retailPrice !== undefined ? change.retailPrice.toFixed(2) : '-'}
                    {change.changes.includes('retailPrice') && (
                      <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                        <Typography variant="caption" sx={{ color: 'text.secondary', textDecoration: 'line-through', mr: 1 }}>
                          {change.originalValues.retailPrice !== undefined ? change.originalValues.retailPrice.toFixed(2) : '-'}
                        </Typography>
                        <Chip size="small" label="تم التغيير" color="warning" variant="outlined" />
                      </Box>
                    )}
                  </TableCell>
                  <TableCell>
                    {change.wholesalePrice !== undefined ? change.wholesalePrice.toFixed(2) : '-'}
                    {change.changes.includes('wholesalePrice') && (
                      <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                        <Typography variant="caption" sx={{ color: 'text.secondary', textDecoration: 'line-through', mr: 1 }}>
                          {change.originalValues.wholesalePrice !== undefined ? change.originalValues.wholesalePrice.toFixed(2) : '-'}
                        </Typography>
                        <Chip size="small" label="تم التغيير" color="warning" variant="outlined" />
                      </Box>
                    )}
                  </TableCell>
                  <TableCell>
                    {change.lastCost !== undefined ? change.lastCost.toFixed(2) : '-'}
                    {change.changes.includes('lastCost') && (
                      <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                        <Typography variant="caption" sx={{ color: 'text.secondary', textDecoration: 'line-through', mr: 1 }}>
                          {change.originalValues.lastCost !== undefined ? change.originalValues.lastCost.toFixed(2) : '-'}
                        </Typography>
                        <Chip size="small" label="تم التغيير" color="warning" variant="outlined" />
                      </Box>
                    )}
                  </TableCell>
                  <TableCell>
                    {change.customPrice1 !== undefined ? change.customPrice1.toFixed(2) : '-'}
                    {change.changes.includes('customPrice1') && (
                      <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                        <Typography variant="caption" sx={{ color: 'text.secondary', textDecoration: 'line-through', mr: 1 }}>
                          {change.originalValues.customPrice1 !== undefined ? change.originalValues.customPrice1.toFixed(2) : '-'}
                        </Typography>
                        <Chip size="small" label="تم التغيير" color="warning" variant="outlined" />
                      </Box>
                    )}
                  </TableCell>
                  <TableCell>
                    {change.customPrice2 !== undefined ? change.customPrice2.toFixed(2) : '-'}
                    {change.changes.includes('customPrice2') && (
                      <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                        <Typography variant="caption" sx={{ color: 'text.secondary', textDecoration: 'line-through', mr: 1 }}>
                          {change.originalValues.customPrice2 !== undefined ? change.originalValues.customPrice2.toFixed(2) : '-'}
                        </Typography>
                        <Chip size="small" label="تم التغيير" color="warning" variant="outlined" />
                      </Box>
                    )}
                  </TableCell>
                  <TableCell>
                    <Tooltip title={change.changes.join(', ')} arrow>
                      <Box>
                        <Chip 
                          icon={<InfoIcon fontSize="small" />}
                          label={`${change.changes.length} تغيير`}
                          size="small" 
                          color="warning"
                        />
                      </Box>
                    </Tooltip>
                  </TableCell>
                </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={changedProducts.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="صفوف في الصفحة:"
        />
      </TableContainer>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" component="h1" gutterBottom>
        تحديث المنتجات بالجملة
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        قم بتنزيل قالب اكسل يحتوي على بيانات المنتجات، ثم قم بتعديل الحقول المطلوبة وإعادة رفع الملف لتحديث المنتجات دفعة واحدة.
      </Typography>
      
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            الخطوة 1: تنزيل قالب الإكسل
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            قم بتنزيل قالب إكسل يحتوي على جميع المنتجات وقيمها الحالية.
          </Typography>
          
          <Button
            variant="contained"
            color="primary"
            startIcon={<DownloadIcon />}
            onClick={downloadTemplate}
            disabled={loadingTemplate}
            sx={{ mt: 1 }}
          >
            تنزيل قالب الإكسل
          </Button>
          
          {loadingTemplate && (
            <Box sx={{ width: '100%', mt: 2 }}>
              <LinearProgress />
            </Box>
          )}
        </CardContent>
      </Card>
      
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            الخطوة 2: تعديل ورفع الملف
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            قم بتعديل الحقول المطلوبة في ملف الإكسل ثم قم بإعادة رفع الملف.
          </Typography>
          
          <Stack direction="row" spacing={2} sx={{ mt: 2, mb: 3 }}>
            <Button
              variant="contained"
              component="label"
              startIcon={<UploadIcon />}
            >
              اختيار ملف
              <input
                type="file"
                hidden
                accept=".xlsx,.xls"
                onChange={handleFileChange}
                ref={fileInputRef}
              />
            </Button>
            
            <Button
              variant="contained"
              color="primary"
              onClick={uploadExcelFile}
              disabled={!file || loading}
            >
              رفع الملف
            </Button>
            
            <Button
              variant="outlined"
              color="secondary"
              onClick={resetForm}
              disabled={loading}
            >
              إعادة تعيين
            </Button>
          </Stack>
          
          {file && (
            <Alert severity="info" sx={{ mb: 2 }}>
              تم اختيار الملف: {file.name}
            </Alert>
          )}
          
          {loading && (
            <Box sx={{ width: '100%', mt: 2 }}>
              <LinearProgress variant="determinate" value={progress} />
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                جاري المعالجة... {progress}%
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>
      
      {errors.length > 0 && (
        <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
          <div>
            {errors.map((error, index) => (
              <div key={index}>{error}</div>
            ))}
          </div>
        </Alert>
      )}
      
      {successMessage && (
        <Alert severity="success" sx={{ mt: 2, mb: 2 }}>
          {successMessage}
        </Alert>
      )}
      
      {isUploaded && productChanges.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              الخطوة 3: مراجعة وتأكيد التغييرات
            </Typography>
            
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary" paragraph>
                راجع التغييرات التالية قبل التأكيد. سيتم عرض فقط المنتجات التي تم تعديلها.
              </Typography>
              
              {renderChangesTable()}
              
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<SaveIcon />}
                  onClick={confirmSave}
                  disabled={loading || productChanges.filter(change => change.isChanged).length === 0}
                  sx={{ minWidth: 200 }}
                >
                  تأكيد التحديثات
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}
      
      <Dialog
        open={showConfirmation}
        onClose={() => setShowConfirmation(false)}
      >
        <DialogTitle>تأكيد التحديث</DialogTitle>
        <DialogContent>
          <DialogContentText>
            هل أنت متأكد من تحديث {productChanges.filter(change => change.isChanged).length} منتج/منتجات؟ 
            هذا الإجراء لا يمكن التراجع عنه.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowConfirmation(false)} color="inherit">
            إلغاء
          </Button>
          <Button onClick={saveChanges} color="primary" variant="contained" autoFocus>
            نعم، تأكيد التحديث
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProductBulkUpdate;